"use client";

import { useEffect, useRef, useState } from "react";
import Fade from "embla-carousel-fade";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { motion } from "framer-motion";

export interface BannerListProps {
  banners: {
    title: string;
    video: string;
    placeholder?: string;
  }[];
  banners_mobile: {
    title: string;
    video: string;
    placeholder?: string;
  }[];
}

export default function BannerList(props: BannerListProps) {
  const [api, setApi] = useState<CarouselApi>();
  const logoRef = useRef<HTMLDivElement>(null);

  const [visibleNavbar, setVisibleNavbar] = useState(false);
  const [desktopVideoReady, setDesktopVideoReady] = useState<Record<number, boolean>>({});
  const [mobileVideoReady, setMobileVideoReady] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setVisibleNavbar(currentY > 0); // scrolling up = show
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!api) return;

    const handleAutoplay = () => {
      const currentIndex = api.selectedScrollSnap();
      const videoEl =
        (document.getElementById(`video-desktop-${currentIndex}`) as
          | HTMLVideoElement
          | null) ??
        (document.getElementById(`video-mobile-${currentIndex}`) as
          | HTMLVideoElement
          | null);
      if (!videoEl) return;

      if (videoEl.ended) {
         videoEl.play().catch(() => undefined);
      }
    };

    api.on("slidesInView", handleAutoplay);
    return () => {
      api.off("slidesInView", handleAutoplay);
    };
  }, [api]);

  useEffect(() => {
    const navbar = document.getElementById("navbar");
    if (!navbar) return;

    if (visibleNavbar) {
      navbar.classList.add("!visible", "!opacity-100");
      if (!logoRef.current) return;
      logoRef.current.classList.add("!invisible", "!opacity-0");
    } else {
      if (!logoRef.current) return;

      logoRef.current.classList.remove("!invisible", "!opacity-0");
      navbar.classList.remove("!visible", "!opacity-100");
    }
  }, [visibleNavbar]);

  return (
    <div className="h-screen relative">
      <button className="w-12 xl:w-24 h-12 xl:h-24 rounded-full inline-flex justify-center items-center border border-white absolute left-1/2 -translate-x-1/2 bottom-3 xl:bottom-6 z-[2]">
        <div className="bg-white rounded-full flex justify-center items-center w-8 xl:w-16 h-8 xl:h-16">
          <motion.div
            animate={{
              y: [0, 10, 0],
            }}
            transition={{
              duration: 1.5,
              ease: "linear",
              repeat: Number.POSITIVE_INFINITY,
              repeatType: "loop",
            }}
          >
            <img
              className="w-4 xl:w-8 h-4 xl:h-8"
              width={32}
              height={32}
              src="/arrow-down.svg"
              alt="chevron-down"
            />
          </motion.div>
        </div>
      </button>

      <div
        ref={logoRef}
        className="absolute left-1/2 -translate-x-1/2 top-14 z-10 transition-all duration-700"
      >
        <img src="/elements-logo.png" alt="" className="object-contain" />
      </div>

      <Carousel
        setApi={setApi}
        plugins={[Fade()]}
        opts={{ loop: true, watchDrag: false }}
        className="hidden xl:block"
      >
        <CarouselContent>
          {props.banners.map((banner, index) => {
            const isLoaded = desktopVideoReady[index] ?? false;

            return (
              <CarouselItem key={index}>
                <div className="relative h-screen">
                  {banner.placeholder ? (
                    <img
                      src={banner.placeholder}
                      alt="banner placeholder"
                      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${isLoaded ? "opacity-0" : "opacity-100"}`}
                      aria-hidden={isLoaded}
                    />
                  ) : (
                    <div
                      className={`absolute inset-0 bg-black transition-opacity duration-700 ease-out ${isLoaded ? "opacity-0" : "opacity-100"}`}
                      aria-hidden="true"
                    />
                  )}

                  <video
                    id={`video-desktop-${index}`}
                    autoPlay={index === 0}
                    muted
                    playsInline
                    webkit-playsinline={"true"}
                    controls={false}
                    preload="auto"
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in ${isLoaded ? "opacity-100" : "opacity-0"}`}
                    onLoadedData={(event) => {
                      setDesktopVideoReady((prev) => ({
                        ...prev,
                        [index]: true,
                      }));
                       event.currentTarget.play().catch(() => undefined);
                    }}
                    onCanPlay={(event) => {
                      setDesktopVideoReady((prev) => ({
                        ...prev,
                        [index]: true,
                      }));
                       event.currentTarget.play().catch(() => undefined);
                    }}
                    onLoadedMetadata={(event) => {
                      if (event.currentTarget.readyState >= 2) {
                        setDesktopVideoReady((prev) => ({
                          ...prev,
                          [index]: true,
                        }));
                         event.currentTarget.play().catch(() => undefined);
                      }
                    }}
                    onEnded={() => {
                      api?.scrollNext();
                    }}
                    ref={(video) => {
                      if (video && video.readyState >= 2 && !isLoaded) {
                        setDesktopVideoReady((prev) => ({
                          ...prev,
                          [index]: true,
                        }));
                        video.play().catch(() => undefined);
                      }
                    }}
                  >
                    <source src={banner.video} type="video/mp4"></source>
                  </video>

                  <div className="absolute top-1/2 w-full z-[2]">
                    <div className="w-full xl:max-w-screen-xl mx-auto">
                      <h1
                        style={{
                          textShadow: "0px 4px 4px rgba(0, 0, 0, 0.45)",
                        }}
                        className="text-white text-center xl:text-left text-2xl md:text-[40px] xl:text-6xl font-pluto xl:leading-12"
                        dangerouslySetInnerHTML={{
                          __html: banner.title.replaceAll("<br/>", "\n"),
                        }}
                      ></h1>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      {/* mobile version */}
      <Carousel
        setApi={setApi}
        opts={{ loop: true, watchDrag: false }}
        plugins={[Fade()]}
        className="block xl:hidden"
      >
        <CarouselContent>
          {props.banners_mobile.map((banner, index) => {
            const isLoaded = mobileVideoReady[index] ?? false;

            return (
              <CarouselItem key={index}>
                <div className="relative h-screen">
                  {banner.placeholder ? (
                    <img
                      src={banner.placeholder}
                      alt="banner placeholder"
                      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${isLoaded ? "opacity-0" : "opacity-100"}`}
                      aria-hidden={isLoaded}
                    />
                  ) : (
                    <div
                      className={`absolute inset-0 bg-black transition-opacity duration-700 ease-out ${isLoaded ? "opacity-0" : "opacity-100"}`}
                      aria-hidden="true"
                    />
                  )}

                  <video
                    id={`video-mobile-${index}`}
                    autoPlay
                    muted
                    playsInline
                    webkit-playsinline={"true"}
                    controls={false}
                    src={banner.video}
                    preload="auto"
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in ${isLoaded ? "opacity-100" : "opacity-0"}`}
                    onLoadedData={(event) => {
                      setMobileVideoReady((prev) => ({
                        ...prev,
                        [index]: true,
                      }));
                       event.currentTarget.play().catch(() => undefined);
                    }}
                    onCanPlay={(event) => {
                      setMobileVideoReady((prev) => ({
                        ...prev,
                        [index]: true,
                      }));
                       event.currentTarget.play().catch(() => undefined);
                    }}
                    onLoadedMetadata={(event) => {
                      if (event.currentTarget.readyState >= 2) {
                        setMobileVideoReady((prev) => ({
                          ...prev,
                          [index]: true,
                        }));
                         event.currentTarget.play().catch(() => undefined);
                      }
                    }}
                    onEnded={() => {
                      api?.scrollNext();
                    }}
                    ref={(video) => {
                      if (video && video.readyState >= 2 && !isLoaded) {
                        setMobileVideoReady((prev) => ({
                          ...prev,
                          [index]: true,
                        }));
                        video.play().catch(() => undefined);
                      }
                    }}
                  ></video>

                  <div className="absolute top-1/2 w-full z-[2]">
                    <div className="w-full xl:max-w-screen-xl mx-auto">
                      <h1
                        style={{
                          textShadow: "0px 4px 4px rgba(0, 0, 0, 0.45)",
                        }}
                        className="text-white text-center xl:text-left text-2xl xl:text-6xl font-pluto xl:leading-20"
                        dangerouslySetInnerHTML={{ __html: banner.title }}
                      ></h1>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
